﻿/**
 * populationEngine.ts
 *
 * TypeScript port of populate_v2.py — coin-change population algorithm.
 * Pure function module: no Qdrant or audio imports.
 *
 * Ticket 20260326f
 * Ticket 20260329g: fill-aware section population
 */

import { generateSongPattern } from './songPatternGenerator';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A sample as known from Qdrant metadata */
export interface SampleMeta {
    file: string;
    bars: number;
    token: string; // 'verse' | 'chorus' | 'intro' | 'ending' | 'pre' | 'bridge'
    isFill: boolean;
    /** Time signature numerator: 4 = 4/4, 3 = 3/4 or 6/8. Defaults to 4. */
    pulse_compas: number;
}

/** A size pool: bar size -> list of filenames */
export type SizePool = Map<number, string[]>;

/** A populated slot: one section with its assigned files */
export interface PopulatedSlot {
    token: string;
    targetBars: number;
    samples: Array<{ file: string; bars: number }>;
}

/** Trace data for a single position within a slot */
export interface PositionTrace {
    size: number;
    availableFiles: string[];
    selectedFile: string;
}

/** Trace data for a single song structure slot */
export interface SlotTrace {
    token: string;
    targetBars: number;
    /** All samples available for this token (primary + fallback, deduplicated) */
    samplesAvailable: Array<{ file: string; bars: number }>;
    sizeCombo: number[];
    positions: PositionTrace[];
}

/** Full generation decision chain for one loop */
export interface GenerationTrace {
    album: string;
    genre: string;
    bpm: number;
    mode: string;
    pattern: Array<{ token: string; bars: number }>;
    slots: SlotTrace[];
    attempts: number;
    fallbackUsed: boolean;
}

/** Result of a population attempt */
export interface PopulationResult {
    complete: boolean;
    slots: PopulatedSlot[];
    attempts: number;
    failedAt?: string;
    fallbackUsed: boolean;
    /** Present only when captureTrace was true and population succeeded */
    trace?: GenerationTrace;
}

// ---------------------------------------------------------------------------
// Token fallback chain
// ---------------------------------------------------------------------------

const TOKEN_FALLBACKS: Record<string, string> = {
    intro:  'verse',
    ending: 'chorus',
    pre:    'verse',
    bridge: 'verse',
};

// ---------------------------------------------------------------------------
// Core functions
// ---------------------------------------------------------------------------

/**
 * Snap a raw computed bar count to the nearest standard musical fraction.
 *
 * Float-drift in `duration / barTime` can produce values like 0.490 or 0.510
 * when the true musical value is 0.5. Without snapping, `buildSizePool` would
 * place these in separate buckets, breaking the coin-change solver for half-bar
 * samples (the most common fill size).
 *
 * Snaps to: 0.25, 0.5, 0.75, then integers 1-32.
 * If no candidate is within `tolerance`, the raw value is returned rounded to 2dp.
 *
 * @param raw       - computed bar count (duration / barTime)
 * @param tolerance - max deviation to snap (default 0.1 — absorbs real audio drift)
 */
export function snapToMusicalBars(raw: number, tolerance: number = 0.1): number {
    // Fractional candidates first (smallest to largest), then integers
    const fractionals = [0.25, 0.5, 0.75];
    for (const c of fractionals) {
        if (Math.abs(raw - c) <= tolerance) return c;
    }
    // Integer candidates 1–32
    const rounded = Math.round(raw);
    if (rounded >= 1 && rounded <= 32 && Math.abs(raw - rounded) <= tolerance) {
        return rounded;
    }
    // No candidate close enough — fall back to 2dp rounding
    return Math.round(raw * 100) / 100;
}

/**
 * Group samples by bar size (rounded to 2 decimal places).
 */
export function buildSizePool(samples: SampleMeta[]): SizePool {
    const pool: SizePool = new Map();
    for (const s of samples) {
        const size = Math.round(s.bars * 100) / 100;
        const existing = pool.get(size);
        if (existing) {
            existing.push(s.file);
        } else {
            pool.set(size, [s.file]);
        }
    }
    return pool;
}

/**
 * Find all size-combinations (with replacement) that sum to target.
 * Uses recursive backtracking. Each size may appear multiple times.
 * Stops once maxCombos results are collected.
 */
export function coinChangeCombos(
    sizes: number[],
    target: number,
    tolerance: number = 0.05,
    maxCombos: number = 1000,
): number[][] {
    const results: number[][] = [];

    function backtrack(start: number, current: number[], currentSum: number): void {
        if (results.length >= maxCombos) return;
        if (Math.abs(currentSum - target) <= tolerance) {
            results.push([...current]);
            return;
        }
        if (currentSum > target + tolerance) return;
        for (let i = start; i < sizes.length; i++) {
            current.push(sizes[i]);
            backtrack(i, current, currentSum + sizes[i]); // i not i+1 — allows repetition
            current.pop();
        }
    }

    backtrack(0, [], 0);
    return results;
}

/**
 * Pick a random file for each size slot in the combo.
 */
export function populateSlot(
    sizeCombo: number[],
    sizePool: SizePool,
): Array<{ file: string; bars: number }> {
    return sizeCombo.map(size => {
        const files = sizePool.get(size)!;
        const file = files[Math.floor(Math.random() * files.length)];
        return { file, bars: size };
    });
}

/**
 * Populate an intro slot: random anchor at START + verse fill at END.
 * If no intro samples exist, fills entirely with verse samples.
 */
export function populateIntro(
    targetBars: number,
    introSamples: SampleMeta[],
    verseSamples: SampleMeta[],
    tolerance: number = 0.05,
    maxCombos: number = 1000,
): Array<{ file: string; bars: number }> | null {
    const fittingIntros = introSamples.filter(s => s.bars <= targetBars + tolerance);
    const anchor = fittingIntros.length > 0
        ? fittingIntros[Math.floor(Math.random() * fittingIntros.length)]
        : null;
    const remaining = Math.round((targetBars - (anchor ? anchor.bars : 0)) * 10000) / 10000;

    if (Math.abs(remaining) <= tolerance) {
        return anchor ? [{ file: anchor.file, bars: anchor.bars }] : [];
    }

    const versePool = buildSizePool(verseSamples);
    if (versePool.size === 0) return null;

    const combos = coinChangeCombos(
        Array.from(versePool.keys()).sort((a, b) => a - b),
        remaining,
        tolerance,
        maxCombos,
    );
    if (combos.length === 0) return null;

    const fill = populateSlot(combos[Math.floor(Math.random() * combos.length)], versePool);
    return anchor ? [{ file: anchor.file, bars: anchor.bars }, ...fill] : fill;
}

/**
 * Populate an ending slot: chorus fill at START + random anchor at END.
 * If no ending samples exist, fills entirely with chorus samples.
 */
export function populateEnding(
    targetBars: number,
    endingSamples: SampleMeta[],
    chorusSamples: SampleMeta[],
    tolerance: number = 0.05,
    maxCombos: number = 1000,
): Array<{ file: string; bars: number }> | null {
    const fittingEndings = endingSamples.filter(s => s.bars <= targetBars + tolerance);
    const anchor = fittingEndings.length > 0
        ? fittingEndings[Math.floor(Math.random() * fittingEndings.length)]
        : null;
    const remaining = Math.round((targetBars - (anchor ? anchor.bars : 0)) * 10000) / 10000;

    if (Math.abs(remaining) <= tolerance) {
        return anchor ? [{ file: anchor.file, bars: anchor.bars }] : [];
    }

    const chorusPool = buildSizePool(chorusSamples);
    if (chorusPool.size === 0) return null;

    const combos = coinChangeCombos(
        Array.from(chorusPool.keys()).sort((a, b) => a - b),
        remaining,
        tolerance,
        maxCombos,
    );
    if (combos.length === 0) return null;

    const fill = populateSlot(combos[Math.floor(Math.random() * combos.length)], chorusPool);
    return anchor ? [...fill, { file: anchor.file, bars: anchor.bars }] : fill;
}

// ---------------------------------------------------------------------------
// Fill-aware section population
// ---------------------------------------------------------------------------

interface FillOption {
    fillSize: number;
    fillFiles: string[];
    bodyCombos: number[][];
}

function _buildFillOptions(
    fillSizePool: SizePool,
    bodyPool: SizePool,
    targetBars: number,
    tolerance: number,
    maxCombos: number,
): FillOption[] {
    const options: FillOption[] = [];
    const sortedBodySizes = Array.from(bodyPool.keys()).sort((a, b) => a - b);

    for (const [fillSize, fillFiles] of fillSizePool) {
        const remaining = Math.round((targetBars - fillSize) * 10000) / 10000;
        if (Math.abs(remaining) <= tolerance) {
            options.push({ fillSize, fillFiles, bodyCombos: [[]] });
        } else if (remaining > tolerance) {
            const combos = coinChangeCombos(sortedBodySizes, remaining, tolerance, maxCombos);
            if (combos.length > 0) {
                options.push({ fillSize, fillFiles, bodyCombos: combos });
            }
        }
    }

    return options;
}

function _buildFromOptions(
    options: FillOption[],
    bodyPool: SizePool,
    fillSizePool: SizePool,
    periodicFill: boolean,
    periodicFillEvery: number,
    periodicFillProbability: number,
    tolerance: number,
): Array<{ file: string; bars: number }> {
    const chosen = options[Math.floor(Math.random() * options.length)];
    const chosenBodyCombo = chosen.bodyCombos[Math.floor(Math.random() * chosen.bodyCombos.length)];

    const bodySlots = populateSlot(chosenBodyCombo, bodyPool);

    if (periodicFill && bodySlots.length > 0) {
        let cumulativeBars = 0;
        let i = 0;

        while (i < bodySlots.length) {
            cumulativeBars += bodySlots[i].bars;

            if (Math.round(cumulativeBars) % periodicFillEvery === 0
                && Math.random() < periodicFillProbability) {

                // Walk back from i to find contiguous slots summing exactly to each fill size.
                // A "clean replacement" means the replaced slots sum equals the fill size.
                const candidates: Array<{ fillSize: number; startIdx: number }> = [];

                for (const fillSize of fillSizePool.keys()) {
                    let acc = 0;
                    let startIdx = i;
                    while (startIdx >= 0) {
                        acc += bodySlots[startIdx].bars;
                        if (Math.abs(acc - fillSize) <= tolerance) {
                            candidates.push({ fillSize, startIdx });
                            break;
                        }
                        if (acc > fillSize + tolerance) break;
                        startIdx--;
                    }
                }

                if (candidates.length > 0) {
                    const { fillSize, startIdx } =
                        candidates[Math.floor(Math.random() * candidates.length)];
                    const fillFiles = fillSizePool.get(fillSize)!;
                    const fillFile = fillFiles[Math.floor(Math.random() * fillFiles.length)];

                    // Replace slots [startIdx..i] with a single fill slot.
                    // cumulativeBars is unchanged (fill has same total bars as replaced slots).
                    bodySlots.splice(startIdx, i - startIdx + 1, { file: fillFile, bars: fillSize });
                    i = startIdx; // next iteration will advance to startIdx + 1
                }
            }

            i++;
        }
    }

    const fillFile = chosen.fillFiles[Math.floor(Math.random() * chosen.fillFiles.length)];
    return [...bodySlots, { file: fillFile, bars: chosen.fillSize }];
}

/**
 * Populate a generic section (verse, chorus, pre, bridge) with fill-first logic.
 *
 * Three-pass algorithm:
 *  Pass 1 (strict):  body uses non-fill samples only; fill goes last.
 *  Pass 2 (relaxed): body may use any sample (fills + non-fills); fill still goes last.
 *                    Handles albums where all samples of a given bar size are fills.
 *  Pass 3 (mixed):   fully mixed coin-change with no fill preference (last resort).
 *
 * When requireNonFill is true, each pass result is validated: if every selected file
 * is a fill (per the isFill Qdrant payload field) and non-fill samples exist, the pass
 * is rejected and execution falls through to the next. Returns null when no valid
 * combination satisfying the constraint exists (caller should treat as failure).
 */
function populateSectionWithFill(
    targetBars: number,
    combinedSamples: SampleMeta[],
    tolerance: number,
    maxCombos: number,
    periodicFill: boolean,
    periodicFillEvery: number,
    periodicFillProbability: number,
    requireNonFill: boolean = false,
): Array<{ file: string; bars: number }> | null {
    const fills = combinedSamples.filter(s => s.isFill);
    const nonFills = combinedSamples.filter(s => !s.isFill);
    const fillSizePool = buildSizePool(fills);
    const nonFillSizePool = buildSizePool(nonFills);
    const allSizePool = buildSizePool(combinedSamples);

    // Set of fill file paths derived from the isFill Qdrant payload field.
    const fillFileSet = new Set(fills.map(s => s.file));
    const isAllFills = (result: Array<{ file: string; bars: number }>): boolean =>
        result.every(s => fillFileSet.has(s.file));

    // Pass 1: strict — body uses non-fills only.
    // With requireNonFill: filter to options with a non-empty body combo (since bodyPool is
    // nonFillSizePool, non-empty body guarantees non-fill files), then iterate options so
    // that periodic fill replacement turning all body slots into fills is also handled.
    const strictOptions = _buildFillOptions(fillSizePool, nonFillSizePool, targetBars, tolerance, maxCombos);
    if (strictOptions.length > 0) {
        if (requireNonFill && nonFills.length > 0) {
            const validStrict = strictOptions.filter(o => o.bodyCombos.some(c => c.length > 0));
            const shuffled = [...validStrict].sort(() => Math.random() - 0.5);
            for (const opt of shuffled) {
                const result = _buildFromOptions([opt], nonFillSizePool, fillSizePool, periodicFill, periodicFillEvery, periodicFillProbability, tolerance);
                if (!isAllFills(result)) return result;
            }
            // All options produced all-fills (e.g. periodic fill replacement converted body) — fall through.
        } else {
            return _buildFromOptions(strictOptions, nonFillSizePool, fillSizePool, periodicFill, periodicFillEvery, periodicFillProbability, tolerance);
        }
    }

    // Pass 2: relaxed — body may use any sample (allSizePool includes fills).
    // With requireNonFill, shuffle options and try each until one yields a non-fill result.
    const relaxedOptions = _buildFillOptions(fillSizePool, allSizePool, targetBars, tolerance, maxCombos);
    if (relaxedOptions.length > 0) {
        if (requireNonFill && nonFills.length > 0) {
            const shuffled = [...relaxedOptions].sort(() => Math.random() - 0.5);
            for (const opt of shuffled) {
                const result = _buildFromOptions([opt], allSizePool, fillSizePool, periodicFill, periodicFillEvery, periodicFillProbability, tolerance);
                if (!isAllFills(result)) return result;
            }
            // Every option produced all-fills — fall through to mixed pass.
        } else {
            return _buildFromOptions(relaxedOptions, allSizePool, fillSizePool, periodicFill, periodicFillEvery, periodicFillProbability, tolerance);
        }
    }

    // Pass 3: fully mixed fallback — no fill preference.
    // With requireNonFill, shuffle combos and try each until one yields a non-fill result.
    const sortedAllSizes = Array.from(allSizePool.keys()).sort((a, b) => a - b);
    const fallbackCombos = coinChangeCombos(sortedAllSizes, targetBars, tolerance, maxCombos);
    if (fallbackCombos.length === 0) return null;
    if (requireNonFill && nonFills.length > 0) {
        const shuffled = [...fallbackCombos].sort(() => Math.random() - 0.5);
        for (const combo of shuffled) {
            const result = populateSlot(combo, allSizePool);
            if (!isAllFills(result)) return result;
        }
        return null; // no combo from this album can satisfy the constraint — caller will retry/change album
    }
    return populateSlot(fallbackCombos[Math.floor(Math.random() * fallbackCombos.length)], allSizePool);
}

// ---------------------------------------------------------------------------
// Internal: single population attempt
// ---------------------------------------------------------------------------

/** Tokens for which a slot must contain at least one non-fill sample. */
const REQUIRE_NON_FILL_TOKENS = new Set(['verse', 'chorus', 'bridge', 'pre']);

interface TryPopulateResult {
    complete: boolean;
    slots: PopulatedSlot[];
    slotTraces?: SlotTrace[];
    failedAt?: string;
}

function _tryPopulate(
    pattern: Array<Record<string, number>>,
    tokenSamples: Record<string, SampleMeta[]>,
    maxCombos: number,
    tolerance: number,
    captureTrace: boolean = false,
    periodicFill: boolean = false,
    periodicFillEvery: number = 4,
    periodicFillProbability: number = 0.25,
): TryPopulateResult {
    const populatedSlots: PopulatedSlot[] = [];
    const slotTraces: SlotTrace[] = [];

    for (const slot of pattern) {
        const tokenType = Object.keys(slot)[0];
        const targetBars = slot[tokenType];

        if (tokenType === 'intro') {
            const introSamples = tokenSamples['intro'] ?? [];
            const verseSamples = tokenSamples['verse'] ?? [];
            const result = populateIntro(targetBars, introSamples, verseSamples, tolerance, maxCombos);
            if (result === null) {
                return { complete: false, slots: populatedSlots, failedAt: `${tokenType}:${targetBars}` };
            }
            populatedSlots.push({ token: tokenType, targetBars, samples: result });
            if (captureTrace) {
                const introPool = buildSizePool([...introSamples, ...verseSamples]);
                slotTraces.push({
                    token: tokenType,
                    targetBars,
                    samplesAvailable: [...introSamples, ...verseSamples].map(s => ({ file: s.file, bars: s.bars })),
                    sizeCombo: result.map(s => s.bars),
                    positions: result.map(s => ({
                        size: s.bars,
                        availableFiles: introPool.get(s.bars) ?? [],
                        selectedFile: s.file,
                    })),
                });
            }

        } else if (tokenType === 'ending') {
            const endingSamples = tokenSamples['ending'] ?? [];
            const chorusSamples = tokenSamples['chorus'] ?? [];
            const result = populateEnding(targetBars, endingSamples, chorusSamples, tolerance, maxCombos);
            if (result === null) {
                return { complete: false, slots: populatedSlots, failedAt: `${tokenType}:${targetBars}` };
            }
            populatedSlots.push({ token: tokenType, targetBars, samples: result });
            if (captureTrace) {
                const endingPool = buildSizePool([...endingSamples, ...chorusSamples]);
                slotTraces.push({
                    token: tokenType,
                    targetBars,
                    samplesAvailable: [...endingSamples, ...chorusSamples].map(s => ({ file: s.file, bars: s.bars })),
                    sizeCombo: result.map(s => s.bars),
                    positions: result.map(s => ({
                        size: s.bars,
                        availableFiles: endingPool.get(s.bars) ?? [],
                        selectedFile: s.file,
                    })),
                });
            }

        } else {
            const primarySamples = tokenSamples[tokenType] ?? [];
            const fallbackToken = TOKEN_FALLBACKS[tokenType];
            const fallbackSamples = fallbackToken ? (tokenSamples[fallbackToken] ?? []) : [];
            const seenFiles = new Set(primarySamples.map(s => s.file));
            const extra = fallbackSamples.filter(s => !seenFiles.has(s.file));
            const combinedSamples = [...primarySamples, ...extra];

            if (combinedSamples.length === 0) {
                return { complete: false, slots: populatedSlots, failedAt: `${tokenType}:${targetBars}` };
            }

            const samples = populateSectionWithFill(
                targetBars, combinedSamples, tolerance, maxCombos, periodicFill, periodicFillEvery, periodicFillProbability,
                REQUIRE_NON_FILL_TOKENS.has(tokenType),
            );
            if (samples === null) {
                return { complete: false, slots: populatedSlots, failedAt: `${tokenType}:${targetBars}` };
            }

            populatedSlots.push({ token: tokenType, targetBars, samples });

            if (captureTrace) {
                const sizePool = buildSizePool(combinedSamples);
                slotTraces.push({
                    token: tokenType,
                    targetBars,
                    samplesAvailable: combinedSamples.map(s => ({ file: s.file, bars: s.bars })),
                    sizeCombo: samples.map(s => s.bars),
                    positions: samples.map(s => ({
                        size: s.bars,
                        availableFiles: sizePool.get(s.bars) ?? [],
                        selectedFile: s.file,
                    })),
                });
            }
        }
    }

    return { complete: true, slots: populatedSlots, slotTraces: captureTrace ? slotTraces : undefined };
}

// ---------------------------------------------------------------------------
// Outer retry loop
// ---------------------------------------------------------------------------

/**
 * Generate a fresh pattern + populate, up to maxAttempts times.
 *
 * Phase 1: try with the provided withIntro/withEnding flags.
 * Phase 2 (fallback): if Phase 1 exhausts all attempts, retry with
 *   withIntro=false, withEnding=false (always drops intro/ending in fallback).
 * Returns on first success, or failure result after both phases exhausted.
 *
 * @param withIntro         - whether to include intro sections (default false — seamless loop mode)
 * @param withEnding        - whether to include ending sections (default false — seamless loop mode)
 * @param captureTrace      - when true, build a GenerationTrace on the successful attempt (dev only)
 * @param traceContext           - album/genre/bpm/mode to embed in the trace (required when captureTrace=true)
 * @param periodicFill           - when true, replace body slots at every periodicFillEvery bars with a fill (default false)
 * @param periodicFillEvery      - bar interval for periodic fill replacement (default 4)
 * @param periodicFillProbability - probability (0–1) that a candidate body slot is replaced with a fill (default 0.25)
 */
export function generateAndPopulate(
    style: string,
    mode: string,
    tokenSamples: Record<string, SampleMeta[]>,
    maxAttempts: number = 250,
    maxCombos: number = 1000,
    tolerance: number = 0.05,
    validBars?: Record<string, number[]>,
    withIntro: boolean = false,
    withEnding: boolean = false,
    captureTrace: boolean = false,
    traceContext?: { album: string; genre: string; bpm: number },
    periodicFill: boolean = false,
    periodicFillEvery: number = 4,
    periodicFillProbability: number = 0.25,
): PopulationResult {
    const safeMode = (mode === 'standard' || mode === 'creative' || mode === 'crazy') ? mode : 'standard';

    // Phase 1: use caller-specified intro/ending flags
    let lastResult: TryPopulateResult = { complete: false, slots: [] };
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const pattern = generateSongPattern(style, safeMode, withIntro, withEnding, validBars);
        const patternAsRecord = pattern.map(slot => slot as Record<string, number>);
        lastResult = _tryPopulate(patternAsRecord, tokenSamples, maxCombos, tolerance, captureTrace, periodicFill, periodicFillEvery, periodicFillProbability);
        if (lastResult.complete) {
            const trace: GenerationTrace | undefined = (captureTrace && lastResult.slotTraces && traceContext)
                ? {
                    album: traceContext.album,
                    genre: traceContext.genre,
                    bpm: traceContext.bpm,
                    mode: safeMode,
                    pattern: patternAsRecord.map(s => ({ token: Object.keys(s)[0], bars: Object.values(s)[0] })),
                    slots: lastResult.slotTraces,
                    attempts: attempt,
                    fallbackUsed: false,
                }
                : undefined;
            return {
                complete: true,
                slots: lastResult.slots,
                attempts: attempt,
                fallbackUsed: false,
                trace,
            };
        }
    }

    // Phase 2: fallback — always skip intro and ending
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const pattern = generateSongPattern(style, safeMode, false, false, validBars);
        const patternAsRecord = pattern.map(slot => slot as Record<string, number>);
        lastResult = _tryPopulate(patternAsRecord, tokenSamples, maxCombos, tolerance, captureTrace, periodicFill, periodicFillEvery, periodicFillProbability);
        if (lastResult.complete) {
            const trace: GenerationTrace | undefined = (captureTrace && lastResult.slotTraces && traceContext)
                ? {
                    album: traceContext.album,
                    genre: traceContext.genre,
                    bpm: traceContext.bpm,
                    mode: safeMode,
                    pattern: patternAsRecord.map(s => ({ token: Object.keys(s)[0], bars: Object.values(s)[0] })),
                    slots: lastResult.slotTraces,
                    attempts: maxAttempts + attempt,
                    fallbackUsed: true,
                }
                : undefined;
            return {
                complete: true,
                slots: lastResult.slots,
                attempts: maxAttempts + attempt,
                fallbackUsed: true,
                trace,
            };
        }
    }

    return {
        complete: false,
        slots: lastResult.slots,
        attempts: maxAttempts * 2,
        failedAt: lastResult.failedAt,
        fallbackUsed: true,
    };
}

// ---------------------------------------------------------------------------
// Populate a caller-supplied fixed pattern (custom mode)
// ---------------------------------------------------------------------------

/**
 * Populate a pre-built pattern directly, without calling generateSongPattern().
 *
 * Used by Custom mode: the user builds the pattern manually and we just need
 * to fill each slot with real samples. Retries up to maxAttempts times.
 *
 * @param pattern       - Array of {section: bars} objects (e.g. [{verse:8},{chorus:8}])
 * @param tokenSamples  - Available samples per token type
 * @param maxAttempts   - Max retry attempts (default 250)
 * @param maxCombos     - Max coin-change combos per slot (default 1000)
 * @param tolerance     - Bar count tolerance (default 0.05)
 * @param captureTrace  - When true, build a GenerationTrace on success
 * @param traceContext  - album/genre/bpm to embed in the trace
 * @param periodicFill  - Enable periodic fill replacement
 * @param periodicFillEvery - Bar interval for periodic fill
 * @param periodicFillProbability - Probability of fill replacement
 */
export function populateFixedPattern(
    pattern: Array<Record<string, number>>,
    tokenSamples: Record<string, SampleMeta[]>,
    maxAttempts: number = 250,
    maxCombos: number = 1000,
    tolerance: number = 0.05,
    captureTrace: boolean = false,
    traceContext?: { album: string; genre: string; bpm: number },
    periodicFill: boolean = false,
    periodicFillEvery: number = 4,
    periodicFillProbability: number = 0.25,
): PopulationResult {
    let lastResult: TryPopulateResult = { complete: false, slots: [] };
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        lastResult = _tryPopulate(pattern, tokenSamples, maxCombos, tolerance, captureTrace, periodicFill, periodicFillEvery, periodicFillProbability);
        if (lastResult.complete) {
            const trace: GenerationTrace | undefined = (captureTrace && lastResult.slotTraces && traceContext)
                ? {
                    album: traceContext.album,
                    genre: traceContext.genre,
                    bpm: traceContext.bpm,
                    mode: 'custom',
                    pattern: pattern.map(s => ({ token: Object.keys(s)[0], bars: Object.values(s)[0] })),
                    slots: lastResult.slotTraces!,
                    attempts: attempt,
                    fallbackUsed: false,
                }
                : undefined;
            return { complete: true, slots: lastResult.slots, attempts: attempt, fallbackUsed: false, trace };
        }
    }
    return {
        complete: false,
        slots: lastResult.slots,
        attempts: maxAttempts,
        failedAt: lastResult.failedAt,
        fallbackUsed: false,
    };
}

// ---------------------------------------------------------------------------
// Helper: compute achievable bar counts from a size pool
// ---------------------------------------------------------------------------

/**
 * Return all bar counts (as multiples of available sizes) achievable by
 * coin-change from the given size pool, up to maxTarget bars.
 *
 * Used by the web app to build validBars before calling generateAndPopulate().
 */
export function computeAchievableTargets(
    sizePool: SizePool,
    maxTarget: number = 32,
    tolerance: number = 0.05,
): number[] {
    const sizes = Array.from(sizePool.keys()).sort((a, b) => a - b);
    if (sizes.length === 0) return [];

    // Build candidate targets: multiples of each available size up to maxTarget
    const candidateSet = new Set<number>();
    for (const size of sizes) {
        let multiple = size;
        while (multiple <= maxTarget + tolerance) {
            candidateSet.add(Math.round(multiple * 100) / 100);
            multiple = Math.round((multiple + size) * 100) / 100;
        }
    }

    // Verify each candidate via coinChangeCombos (maxCombos=1 for speed)
    const achievable: number[] = [];
    for (const target of Array.from(candidateSet).sort((a, b) => a - b)) {
        if (target > maxTarget + tolerance) continue;
        const combos = coinChangeCombos(sizes, target, tolerance, 1);
        if (combos.length > 0) {
            achievable.push(target);
        }
    }
    return achievable;
}
